/**
 * Strings of AutoMod and slowmode (2026-10-29): Server settings > AutoMod
 * (Discord's three rule cards), the alert message in the alert channel, the
 * note a blocked sender sees, the audit lines, and the channel settings'
 * slowmode control with the composer's countdown.
 */
import type { Dict } from './core';

export const automodEn: Dict = {
  // the tab
  'am.tab': 'AutoMod',
  'am.intro':
    'AutoMod checks messages before anyone sees them. Administrators, people with Manage Server and the owner are never checked.',
  'am.off': 'AutoMod is turned off by the admins.',
  'am.loadFail': "Couldn't load the rules.",
  'am.retry': 'Try again',
  'am.saved': 'Rule saved',
  'am.deleted': 'Rule deleted',
  'am.new': 'Create rule',
  'am.edit': 'Edit',
  'am.delete': 'Delete rule',
  'am.deleteTitle': 'Delete {name}?',
  'am.deleteBody': 'AutoMod stops checking messages for this rule.',
  'am.enabled': 'Rule on',
  'am.cancel': 'Cancel',
  'am.save': 'Save rule',
  'am.limitReached': 'This server has the most rules of this kind ({n}).',

  // the three kinds
  'am.kw.title': 'Block custom words',
  'am.kw.sub': 'Up to 6 lists of words and phrases.',
  'am.kw.default': 'Custom words',
  'am.mention.title': 'Block mention spam',
  'am.mention.sub': 'Messages that mention too many people or roles.',
  'am.mention.default': 'Mention spam',
  'am.spam.title': 'Block spam-like content',
  'am.spam.sub': 'The same message sent {n} times within a minute.',
  'am.spam.default': 'Spam-like content',

  // a rule card
  'am.summary.words': { one: '{n} word', other: '{n} words' },
  'am.summary.mentions': 'More than {n} mentions',
  'am.summary.blocks': 'Blocks',
  'am.summary.alerts': 'Alerts #{channel}',
  'am.summary.timeout': 'Timeout {time}',
  'am.summary.off': 'Off',

  // the editor
  'am.f.name': 'Rule name',
  'am.f.words': 'Words and phrases',
  'am.f.wordsHelp':
    'One per line or separated by commas. word matches the whole word, word* words starting with it, *word words ending with it, *word* anywhere. Case and keyboard differences are ignored.',
  'am.f.wordsCount': '{n} of {max}',
  'am.f.allow': 'Allowed words',
  'am.f.allowHelp': 'Never caught, even inside a blocked word (same wildcards).',
  'am.f.mentions': 'Most mentions in one message',
  'am.f.mentionsHelp': 'Unique people and roles, 1 to 50.',
  'am.f.spamHelp':
    'Catches the same text from one person {n} times within a minute, in any channel. The first copies go through.',
  'am.f.actions': 'Responses',
  'am.f.block': 'Block the message',
  'am.f.blockHelp': 'The sender sees that it was blocked.',
  'am.f.custom': 'Custom response (optional)',
  'am.f.customPh': 'Shown to the sender. Up to 150 characters.',
  'am.f.alert': 'Send an alert',
  'am.f.alertHelp': 'Moderators see the message, who sent it and the rule.',
  'am.f.alertChannel': 'Alert channel',
  'am.f.pickChannel': 'Pick a channel',
  'am.f.timeout': 'Time out the member',
  'am.f.timeoutHelp': 'They can’t send messages, react or join voice until it ends.',
  'am.f.timeoutNeeds': 'Needs Moderate Members.',
  'am.f.timeoutFor': 'Timeout length',
  'am.f.exempt': 'Exemptions',
  'am.f.exemptRoles': 'Exempt roles',
  'am.f.exemptChannels': 'Exempt channels and categories',
  'am.f.none': 'None',
  'am.f.add': 'Add',
  'am.f.remove': 'Remove {name}',
  'am.err.noWords': 'Add at least one word.',
  'am.err.badWord': 'Each word needs a letter or number, at most 60 characters.',
  'am.err.tooMany': 'At most {n} words.',
  'am.err.noAction': 'Pick at least one response.',
  'am.err.noChannel': 'Pick the alert channel.',

  // the alert message
  'am.alert.by': 'AutoMod',
  'am.alert.blocked': 'blocked a message in {channel}',
  'am.alert.flagged': 'flagged a message in {channel}',
  'am.alert.rule': 'Rule',
  'am.alert.keyword': 'Keyword',
  'am.alert.matched': 'Matched',
  'am.alert.timeout': 'Timed out',
  'am.alert.minutes': { one: '{n} minute', other: '{n} minutes' },
  'am.alert.jump': 'Jump to message',
  'am.alert.deleteMsg': 'Delete message',
  'am.alert.deletedMsg': 'Message deleted',
  'am.alert.timeoutMember': 'Time out',
  'am.alert.kick': 'Kick',
  'am.alert.ban': 'Ban',
  'am.alert.actions': 'Actions',
  'am.alert.deletedChannel': 'a deleted channel',

  // the sender's note
  'am.blocked.title': 'AutoMod blocked this message.',
  'am.blocked.only': 'Only you can see this.',
  'am.blocked.copy': 'Copy text',
  'am.blocked.copied': 'Copied',
  'am.blocked.dismiss': 'Dismiss',

  // errors (ns.err.<code>)
  'ns.err.200000': 'AutoMod blocked this message.',
  'ns.err.200001': 'AutoMod blocked this title.',
  'ns.err.50021': 'This is a system message: it can’t be edited.',

  // the audit log
  'am.audit.create': '{actor} created the AutoMod rule {rule}',
  'am.audit.update': '{actor} changed the AutoMod rule {rule}',
  'am.audit.delete': '{actor} deleted the AutoMod rule {rule}',
  'am.audit.block': 'AutoMod blocked a message from {target} in {channel} ({rule})',
  'am.audit.flag': 'AutoMod flagged a message from {target} in {channel} ({rule})',
  'am.audit.timeout': 'AutoMod timed out {target} until {time} ({rule})',
  'am.audit.f': 'AutoMod actions',

  // slowmode
  'sm.label': 'Slowmode',
  'sm.help': 'How long each member waits between messages. Moderators are not slowed.',
  'sm.off': 'Off',
  'sm.s': { one: '{n} second', other: '{n} seconds' },
  'sm.m': { one: '{n} minute', other: '{n} minutes' },
  'sm.h': { one: '{n} hour', other: '{n} hours' },
  'sm.every': 'Slowmode: one message every {time}',
  'sm.wait': 'You can send again in {time}',
  'sm.waitToast': 'Slowmode: wait {time}',
};
